import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Video,
  Calendar as CalendarIcon,
  Loader2,
  CheckCircle,
  Phone,
  User,
  FileText,
  ArrowLeft,
  Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/calendar';
import { cn } from '@/lib/utils';
import { intakeApi, type IntakeSlot } from '@/features/intake/services/intakeApi';
import {
  buildIntakeScheduleMessage,
  dateKeyInTimezone,
} from '@/features/intake/utils/scheduleMessage';
import { SEO } from '@/shared/components/SEO';

type Step = 'form' | 'path' | 'schedule' | 'confirmation';

function parseDateKey(dateKey: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function PublicIntakePage() {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [contractorName, setContractorName] = useState('');
  const [schedulingAvailable, setSchedulingAvailable] = useState(false);

  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    project: '',
  });

  const [slots, setSlots] = useState<IntakeSlot[]>([]);
  const [slotsTimezone, setSlotsTimezone] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [messageEdited, setMessageEdited] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  useEffect(() => {
    if (!contractorId) return;
    (async () => {
      try {
        const res = await intakeApi.getContractor(contractorId);
        setContractorName(res.contractor.name);
        setSchedulingAvailable(res.contractor.schedulingAvailable);
      } catch {
        setError('This link is not valid. Please check the URL and try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [contractorId]);

  /** Re-check scheduling when path step is shown (avoids stale state if hours were saved after page load) */
  useEffect(() => {
    if (step !== 'path' || !contractorId) return;
    intakeApi.getContractor(contractorId).then((res) => {
      setSchedulingAvailable(res.contractor.schedulingAvailable);
      setContractorName(res.contractor.name);
    }).catch(() => {});
  }, [step, contractorId]);

  const availableDateKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const slot of slots) {
      keys.add(dateKeyInTimezone(slot.start, slotsTimezone || 'America/New_York'));
    }
    return keys;
  }, [slots, slotsTimezone]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate || !slotsTimezone) return [];
    const key = dateKeyInTimezone(selectedDate, slotsTimezone);
    return slots.filter(
      (slot) => dateKeyInTimezone(slot.start, slotsTimezone) === key
    );
  }, [slots, selectedDate, slotsTimezone]);

  const messagePreview = useMemo(
    () =>
      buildIntakeScheduleMessage(
        formData.firstName,
        selectedSlot,
        slotsTimezone || 'America/New_York'
      ),
    [formData.firstName, selectedSlot, slotsTimezone]
  );

  useEffect(() => {
    if (!messageEdited) {
      setScheduleMessage(messagePreview);
    }
  }, [messagePreview, messageEdited]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractorId) return;

    const phone = formData.phoneNumber.replace(/\D/g, '');
    if (!formData.firstName.trim() || phone.length < 10) {
      setError('Please enter your first name and a valid phone number.');
      return;
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await intakeApi.submitForm({
        contractorId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim() || undefined,
        phoneNumber: formData.phoneNumber,
        email: formData.email.trim() || undefined,
        project: formData.project.trim() || undefined,
      });
      setSubmissionId(res.submissionId);
      setContractorName(res.contractorName);
      setSchedulingAvailable(res.schedulingAvailable);
      setStep('path');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordVideo = async () => {
    if (!submissionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await intakeApi.startRecording(submissionId);
      navigate(`/record/${res.roomId}?direct=1`);
    } catch (err: any) {
      setError(err.message || 'Failed to start recording. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSchedulePath = async () => {
    if (!submissionId) return;
    setStep('schedule');
    setSlotsLoading(true);
    setError(null);
    setSelectedSlot(null);
    setSelectedDate(undefined);
    setMessageEdited(false);
    try {
      const res = await intakeApi.getSlots(submissionId);
      setSlots(res.slots);
      setSlotsTimezone(res.timezone || 'America/New_York');
      if (res.slots.length === 0) {
        setError('No available time slots in the next 2 weeks. Please try recording a video instead.');
      } else {
        const tz = res.timezone || 'America/New_York';
        const firstKey = dateKeyInTimezone(res.slots[0].start, tz);
        setSelectedDate(parseDateKey(firstKey));
        setSelectedSlot(res.slots[0].start);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load available times.');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setMessageEdited(false);
    if (date && slotsTimezone) {
      const key = dateKeyInTimezone(date, slotsTimezone);
      const matching = slots.filter(
        (s) => dateKeyInTimezone(s.start, slotsTimezone) === key
      );
      setSelectedSlot(matching[0]?.start ?? null);
    } else {
      setSelectedSlot(null);
    }
    if (date) setIsDatePickerOpen(false);
  };

  const handleBookSlot = async () => {
    if (!submissionId || !selectedSlot) return;
    setSubmitting(true);
    setError(null);
    try {
      await intakeApi.scheduleCall(
        submissionId,
        selectedSlot,
        scheduleMessage.trim() || messagePreview
      );
      const slotLabel =
        slots.find((s) => s.start === selectedSlot)?.label || '';
      setConfirmationMessage(
        `Your call with ${contractorName} is confirmed for ${slotLabel}. You'll receive a text message with the join link.`
      );
      setStep('confirmation');
    } catch (err: any) {
      setError(err.message || 'Failed to book this time. Please pick another slot.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && step === 'form' && !contractorName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.href = 'https://www.wlkthru.co'}>
              Go to wlkthru.co
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO title={`Contact ${contractorName}`} description={`Get in touch with ${contractorName}`} />

      <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <span className="text-2xl font-bold text-primary">
              {contractorName.charAt(0).toUpperCase()}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{contractorName}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Tell us about your project and how you'd like to connect
          </p>
        </div>

        {error && step !== 'form' && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        {step === 'form' && (
          <Card>
            <CardContent className="pt-6 pb-6">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="firstName"
                      className="pl-9"
                      placeholder="Your first name"
                      value={formData.firstName}
                      onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Your last name (optional)"
                    value={formData.lastName}
                    onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      className="pl-9"
                      placeholder="(555) 123-4567"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData((p) => ({ ...p, phoneNumber: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      className="pl-9"
                      placeholder="your@email.com (optional)"
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project">Brief Description of the Job</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Textarea
                      id="project"
                      className="pl-9 min-h-[80px]"
                      placeholder="Describe what you need help with (optional)"
                      value={formData.project}
                      onChange={(e) => setFormData((p) => ({ ...p, project: e.target.value }))}
                    />
                  </div>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 'path' && (
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground mb-2">
              How would you like to connect with {contractorName}?
            </p>

            <button
              type="button"
              onClick={handleRecordVideo}
              disabled={submitting}
              className="w-full text-left"
            >
              <Card className="hover:border-primary/50 hover:shadow-md transition-all cursor-pointer">
                <CardContent className="pt-6 pb-6 flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/30">
                    <Video className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Record a Video</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Film the damage or job using your phone camera and submit the recording.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </button>

            {schedulingAvailable ? (
              <button
                type="button"
                onClick={handleSchedulePath}
                disabled={submitting}
                className="w-full text-left"
              >
                <Card className="hover:border-primary/50 hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="pt-6 pb-6 flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30">
                      <CalendarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Schedule a Call</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Pick a date and time for a live video call with {contractorName}.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </button>
            ) : (
              <Card className="opacity-60">
                <CardContent className="pt-6 pb-6 flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800">
                    <CalendarIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-muted-foreground">Schedule a Call</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Not available yet — the contractor needs to set working hours in Settings
                      under &quot;Public Intake — Working Hours&quot; before customers can book calls.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => { setStep('form'); setError(null); }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        )}

        {step === 'schedule' && (
          <Card>
            <CardContent className="pt-6 pb-6 space-y-4">
              <div>
                <h2 className="font-semibold text-lg mb-1">Schedule a Call</h2>
                <p className="text-sm text-muted-foreground">
                  Choose when you&apos;d like to meet with {contractorName}
                  {slotsTimezone && (
                    <span className="block mt-1 text-xs">
                      Times shown in {slotsTimezone.replace(/_/g, ' ')}
                    </span>
                  )}
                </p>
              </div>

              {slotsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : slots.length === 0 ? (
                <p className="text-center text-muted-foreground py-6">
                  No available slots. Try recording a video instead.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Select Date</Label>
                      <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !selectedDate && 'text-muted-foreground'
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              if (date < today) return true;
                              const key = dateKeyInTimezone(date, slotsTimezone);
                              return !availableDateKeys.has(key);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Select Time</Label>
                      <Select
                        value={selectedSlot ?? undefined}
                        onValueChange={(value) => {
                          setSelectedSlot(value);
                          setMessageEdited(false);
                        }}
                        disabled={slotsForSelectedDate.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {slotsForSelectedDate.map((slot) => (
                            <SelectItem key={slot.start} value={slot.start}>
                              {new Date(slot.start).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZone: slotsTimezone,
                              })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      value={scheduleMessage || messagePreview}
                      onChange={(e) => {
                        setScheduleMessage(e.target.value);
                        setMessageEdited(true);
                      }}
                      className="min-h-[100px] text-sm"
                      placeholder="Your confirmation message..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Updates automatically when you change the date or time. The join link is added when the text is sent.
                    </p>
                  </div>
                </>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setStep('path'); setError(null); }}>
                  Back
                </Button>
                <Button
                  className="flex-1"
                  disabled={!selectedSlot || submitting || slots.length === 0}
                  onClick={handleBookSlot}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    'Schedule Call'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'confirmation' && (
          <Card>
            <CardContent className="pt-8 pb-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">You&apos;re all set!</h2>
              <p className="text-muted-foreground text-sm">{confirmationMessage}</p>
              <p className="text-xs text-muted-foreground mt-4">Powered by wlkthru</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground mt-8">
          Powered by{' '}
          <a href="https://www.wlkthru.co" className="underline hover:text-foreground">
            wlkthru
          </a>
        </p>
      </div>
    </div>
  );
}
